import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Info, Lock } from "lucide-react";
import api from "../lib/api";
import { track } from "../lib/usageTracker";
import { useAuth } from "../context/AuthContext";
import { ActionButton, SecondaryAction } from "../components/kit";
import PayChrome, { WithTokens, PlaceholderToken } from "../components/PayChrome";
import {
  BACK_TO_SETTINGS,
  CHOOSE_PLAN_AGAIN,
  FAILED_ACCOUNT_LABEL,
  FAILED_EYEBROW,
  FAILED_FOOT,
  FAILED_KEEPS,
  FAILED_LEDE,
  FAILED_STRIPE,
  FAILED_TITLE,
  FAILED_UNTIL,
  FLOW_CURRENT,
  FLOW_FAILED,
  GUARDIAN_NEEDS_PLAN_PAGE,
  PLAN_NAME,
  RETRYING,
  RETRY_COULD_NOT_OPEN,
  TOKEN,
  TRY_AGAIN_LABEL,
  failedAccountLine,
  priceFor,
  priceWithFee,
} from "../lib/payCopy";

/**
 * Back from Stripe, not paid (`/plan/failed`, #1036, D204.2), behind
 * `newUi("pay")`. The cancel_url of every Checkout session lands here.
 *
 * Says in order what still exists (the account), how to try again (the one
 * gold), and the way on that does not lose the account. The refusal line is
 * alert-styled (D38, D55). Nothing here claims a reason: Stripe does not
 * always say why, so the line is the common case, said as the common case.
 *
 * Try again asks for a fresh Checkout session for the same period the account
 * last chose (`GET /billing/status`). An 11-to-17 player's guardian email is
 * not kept in full, so for them the server asks for it again and this page
 * sends them to `/plan` to enter it. States: `retrying` (busy, inert) and
 * `retry-could-not-open`.
 */
const card = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: 14,
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line)",
  borderRadius: 12,
};
const eyebrow = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--nw-ink-muted)",
};

export default function PaymentFailedPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [price, setPrice] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [couldNotOpen, setCouldNotOpen] = useState(false);
  const [needsPlanPage, setNeedsPlanPage] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    track("plan.return_failed");
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await api.get("/billing/status");
        if (!live) return;
        setStatus(data);
        if (data?.plan_code) {
          const { data: plans } = await api.get("/billing/plans");
          const hit = priceFor(plans?.plans, data.plan_code, data.period);
          // D420: the total, plan plus card fee; nothing when the row holds no fee.
          const total = priceWithFee(hit)?.total;
          if (live && total) setPrice(total);
        }
      } catch {
        /* the page still says everything it needs without them */
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const retry = useCallback(async () => {
    if (retrying) return;
    setCouldNotOpen(false);
    setNeedsPlanPage(false);
    setRetrying(true);
    try {
      const { data } = await api.post("/billing/checkout", { period: status?.period || "monthly" });
      if (typeof data?.url === "string" && data.url) {
        window.location.assign(data.url);
        return;
      }
      throw new Error("no url");
    } catch (e) {
      if (!mounted.current) return;
      const code = e?.response?.data?.code;
      if (code === "payer_email_required" || code === "already_subscribed" || code === "dob_required") setNeedsPlanPage(true);
      else setCouldNotOpen(true);
      setRetrying(false);
    }
  }, [retrying, status]);

  const name = user?.display_name || user?.full_name || "";

  return (
    <PayChrome screenTestId="payment-failed-new-screen" flow={FLOW_FAILED} current={FLOW_CURRENT.failed} flowTestId="payment-failed-flow" inert={retrying}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div className="nw-eyebrow">{FAILED_EYEBROW}</div>
        <h1 className="nw-title-lg" style={{ margin: 0 }} data-testid="payment-failed-title">
          {FAILED_TITLE}
        </h1>
        <div
          role="alert"
          data-testid="payment-failed-refused"
          style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 12px", background: "var(--nw-line-faint)", borderLeft: "3px solid var(--nw-alert)", borderRadius: 10, fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink)" }}
        >
          <Info size={16} color="#B42318" aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{FAILED_LEDE}</span>
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-lede)" }} data-testid="payment-failed-stripe">
          <Lock size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{FAILED_STRIPE}</span>
        </div>
      </div>

      <div style={card} data-testid="payment-failed-account">
        <div style={eyebrow}>{FAILED_ACCOUNT_LABEL}</div>
        <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink)" }}>
          {name && user?.email ? failedAccountLine(name, user.email) : <WithTokens text={failedAccountLine(TOKEN.PLAYER_NAME, TOKEN.GUARDIAN_EMAIL)} />}
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-lede)" }}>{FAILED_KEEPS}</div>
      </div>

      <div style={{ ...card, flexDirection: "row", justifyContent: "space-between", alignItems: "center" }} data-testid="payment-failed-plan">
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={eyebrow}>
            <PlaceholderToken>{TOKEN.PLAN}</PlaceholderToken>
          </span>
          <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 16, color: "var(--nw-ink)" }}>
            {PLAN_NAME[status?.plan_code] || ""}
          </span>
        </div>
        {price && <strong style={{ fontSize: 16 }}>{price}</strong>}
      </div>

      {couldNotOpen && (
        <div role="alert" data-testid="retry-could-not-open" style={{ padding: "10px 12px", background: "var(--nw-line-faint)", borderLeft: "3px solid var(--nw-alert)", borderRadius: 10, fontSize: 13, lineHeight: 1.5 }}>
          {RETRY_COULD_NOT_OPEN}
        </div>
      )}
      {needsPlanPage && (
        <div role="alert" data-testid="retry-needs-plan-page" style={{ padding: "10px 12px", background: "var(--nw-line-faint)", borderLeft: "3px solid var(--nw-alert)", borderRadius: 10, fontSize: 13, lineHeight: 1.5 }}>
          {GUARDIAN_NEEDS_PLAN_PAGE} <Link to="/plan">{CHOOSE_PLAN_AGAIN}</Link>
        </div>
      )}

      <ActionButton
        data-testid="payment-retry"
        onClick={retry}
        aria-disabled={retrying || undefined}
        style={retrying ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
      >
        {retrying ? <span data-testid="retrying">{RETRYING}</span> : TRY_AGAIN_LABEL}
      </ActionButton>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-lede)" }} data-testid="payment-failed-until">
          {FAILED_UNTIL}
        </div>
        <SecondaryAction variant="navy-outline" onClick={() => !retrying && nav("/settings")} aria-disabled={retrying || undefined} data-testid="payment-back-settings">
          {BACK_TO_SETTINGS}
        </SecondaryAction>
      </div>

      <div style={{ fontSize: 11, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>{FAILED_FOOT}</div>
    </PayChrome>
  );
}
